import { useEffect, useState } from "react";
import type { ExperimentConfig, OptimizelyEvent } from "stores/OptimizelyStore";
import { useOptimizelyStore } from "stores/OptimizelyStore";
import config from "config/config";
import { appendExternalScript } from "utils/appendExternalScript/appendExternalScript";
import { useLanguage } from "contexts/LocalizationContext";
import useExtendedRouter from "hooks/useExtendedRouter";
import { useOptimizelyExperiments } from "contexts/DataContext/hooks";
import type { OptimizelyTransformedExperiment } from "services/optimizely/types";
import {
  getIncludeDraftExperiments,
  getOptimizelyVariations,
  pushConversionEvent,
  setIncludeDraftExperiments,
  shouldInsertOptimizelySnippet,
} from "./utils";

const {
  optimizely: { scriptUrl },
} = config;

const useIsSupportedPage = (experiments: OptimizelyTransformedExperiment[], forceDraftExperiments: boolean) => {
  const language = useLanguage();
  const router = useExtendedRouter();

  return shouldInsertOptimizelySnippet(experiments, router, language, forceDraftExperiments);
}

export const useOptimizelySnippet = (experiments: OptimizelyTransformedExperiment[]) => {
  const { setOptimizelyLoading, setOptimizelyLoaded, setOptimizelyError } = useOptimizelyStore();
  const [forceDraftExperiments, setForceDraftExperiments] = useState<boolean>(false);
  const isSupportedPage = useIsSupportedPage(experiments, forceDraftExperiments);

  useEffect(() => {
    setIncludeDraftExperiments();
    if (getIncludeDraftExperiments()) setForceDraftExperiments(true);
    if (!isSupportedPage) {
      return;
    }

    appendExternalScript(scriptUrl, {}, setOptimizelyLoading, setOptimizelyLoaded, setOptimizelyError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupportedPage]);
};

export const useOptimizely = (experimentConfig: ExperimentConfig) => {
  const experiments = useOptimizelyExperiments();
  const { isLoading, isLoaded, hasError } = useOptimizelyStore();
  const [variationIndex, setVariationIndex] = useState<number>(null);
  const [variationId, setVariationId] = useState<number>(null);
  const [forceDraftExperiments, setForceDraftExperiments] = useState<boolean>(false);
  const experiment = experiments.find((experiment) => experiment.id === experimentConfig.experimentId);
  const isSupportedPage = useIsSupportedPage(experiments, forceDraftExperiments);

  const setOptimizelyVariation = (event: OptimizelyEvent, experiment: OptimizelyTransformedExperiment) => {
    if (hasError || !experiment || experiment?.variations?.length === 0) {
      return;
    }

    const { variationId } = event.data.decision;
    setVariationId(Number(variationId));
    const index = experiment?.variations?.findIndex((variationIndex) => variationIndex.variation_id === Number(variationId));
    setVariationIndex(index === -1 ? null : index);
  };

  useEffect(() => {
    if (!experiment) {
      return;
    }

    setIncludeDraftExperiments();
    if (getIncludeDraftExperiments()) setForceDraftExperiments(true);
    getOptimizelyVariations((event) => setOptimizelyVariation(event, experiment));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experiment]);

  if (!isSupportedPage) return {};

  return {
    variationIndex: experiment ? variationIndex : null,
    variationId: experiment ? variationId : null,
    pushConversionEvent: pushConversionEvent(experimentConfig),
    isLoading,
    isLoaded,
  };
};
